import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchBinanceP2P,
  fetchGateP2P,
  fetchBybitP2P,
  fetchBitgetP2P,
  fetchKucoinP2P,
  DEFAULT_P2P_LIMIT,
} from "../services/p2p";

const CACHE_TTL = 15 * 1000;
const cache = new Map();

const buildKey = (side, limit) => `${side}|${limit}`;

const readCache = (key) => {
  const entry = cache.get(key);
  if (entry && entry.expires > Date.now()) {
    return entry.data;
  }
  cache.delete(key);
  return null;
};

const writeCache = (key, data) => {
  cache.set(key, { expires: Date.now() + CACHE_TTL, data });
};

export default function useP2PQuotes({ side = "SELL", limit = DEFAULT_P2P_LIMIT } = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFailures, setHasFailures] = useState(false);
  const pendingFetches = useRef({});

  const combos = useMemo(
    () => [
      {
        exchange: "binance",
        fiat: "BRL",
        label: "Binance BRL",
        experimental: false,
        fetcher: (requestedSide) =>
          fetchBinanceP2P({
            fiat: "BRL",
            side: requestedSide,
            rows: limit,
            payTypes: ["PIX"],
          }),
      },
      {
        exchange: "binance",
        fiat: "BOB",
        label: "Binance BOB",
        experimental: false,
        fetcher: (requestedSide) =>
          fetchBinanceP2P({
            fiat: "BOB",
            side: requestedSide,
            rows: limit,
          }),
      },
      {
        exchange: "gate",
        fiat: "BRL",
        label: "Gate BRL",
        experimental: false,
        fetcher: (requestedSide) =>
          fetchGateP2P({
            fiat: "BRL",
            side: requestedSide === "SELL" ? "sell" : "buy",
            limit,
          }),
      },
      {
        exchange: "gate",
        fiat: "BOB",
        label: "Gate BOB",
        experimental: false,
        fetcher: (requestedSide) =>
          fetchGateP2P({
            fiat: "BOB",
            side: requestedSide === "SELL" ? "sell" : "buy",
            limit,
          }),
      },
      {
        exchange: "bybit",
        fiat: "BRL",
        label: "Bybit BRL",
        experimental: false,
        fetcher: (requestedSide) =>
          fetchBybitP2P({
            fiat: "BRL",
            side: requestedSide,
            size: limit,
          }),
      },
      {
        exchange: "bybit",
        fiat: "BOB",
        label: "Bybit BOB",
        experimental: false,
        fetcher: (requestedSide) =>
          fetchBybitP2P({
            fiat: "BOB",
            side: requestedSide,
            size: limit,
          }),
      },
      {
        exchange: "bitget",
        fiat: "BRL",
        label: "Bitget BRL",
        experimental: false,
        fetcher: (requestedSide) =>
          fetchBitgetP2P({
            fiat: "BRL",
            side: requestedSide,
            pageSize: limit,
          }),
      },
      {
        exchange: "bitget",
        fiat: "BOB",
        label: "Bitget BOB",
        experimental: false,
        fetcher: (requestedSide) =>
          fetchBitgetP2P({
            fiat: "BOB",
            side: requestedSide,
            pageSize: limit,
          }),
      },
      {
        exchange: "kucoin",
        fiat: "BRL",
        label: "KuCoin BRL",
        experimental: true,
        fetcher: (requestedSide) =>
          fetchKucoinP2P({
            fiat: "BRL",
            side: requestedSide,
            pageSize: limit,
          }),
      },
      {
        exchange: "kucoin",
        fiat: "BOB",
        label: "KuCoin BOB",
        experimental: true,
        fetcher: (requestedSide) =>
          fetchKucoinP2P({
            fiat: "BOB",
            side: requestedSide,
            pageSize: limit,
          }),
      },
    ],
    [limit]
  );

  const fetchSideData = useCallback(
    async (requestedSide, force = false) => {
      const cacheKey = buildKey(requestedSide, limit);
      if (!force) {
        const cached = readCache(cacheKey);
        if (cached) {
          return cached;
        }
      }
      if (pendingFetches.current[cacheKey]) {
        return pendingFetches.current[cacheKey];
      }
      const promise = (async () => {
        const settled = await Promise.allSettled(
          combos.map((combo) => combo.fetcher(requestedSide))
        );
        const entries = combos.map((combo, index) => {
          const outcome = settled[index];
          if (outcome.status === "fulfilled") {
            return {
              label: combo.label,
              exchange: combo.exchange,
              fiat: combo.fiat,
              experimental: combo.experimental,
              quote: outcome.value,
              side: requestedSide,
              error: null,
            };
          }
          return {
            label: combo.label,
            exchange: combo.exchange,
            fiat: combo.fiat,
            experimental: combo.experimental,
            quote: null,
            side: requestedSide,
            error: (outcome.reason && outcome.reason.message) || "Indisponível",
          };
        });
        writeCache(cacheKey, entries);
        return entries;
      })();
      pendingFetches.current[cacheKey] = promise;
      try {
        return await promise;
      } finally {
        delete pendingFetches.current[cacheKey];
      }
    },
    [combos, limit]
  );

  const fetchQuotes = useCallback(
    async (force = false) => {
      const cachedSell = !force && readCache(buildKey("SELL", limit));
      const cachedBuy = !force && readCache(buildKey("BUY", limit));
      if (cachedSell && cachedBuy) {
        const combined = combos.map((combo, index) => ({
          exchange: combo.exchange,
          fiat: combo.fiat,
          label: combo.label,
          experimental: combo.experimental,
          sellQuote: cachedSell[index]?.quote ?? null,
          sellError: cachedSell[index]?.error ?? null,
          buyQuote: cachedBuy[index]?.quote ?? null,
          buyError: cachedBuy[index]?.error ?? null,
        }));
        setData(combined);
        setHasFailures(
          cachedSell.some((entry) => entry?.error) ||
            cachedBuy.some((entry) => entry?.error)
        );
        return combined;
      }
      setLoading(true);
      setHasFailures(false);
      try {
        const [sellEntries, buyEntries] = await Promise.all([
          fetchSideData("SELL", force),
          fetchSideData("BUY", force),
        ]);
        const failureEntries = [
          ...sellEntries
            .filter((entry) => entry?.error)
            .map((entry) => ({ ...entry, side: entry.side || "SELL" })),
          ...buyEntries
            .filter((entry) => entry?.error)
            .map((entry) => ({ ...entry, side: entry.side || "BUY" })),
        ];
        if (failureEntries.length) {
          const message = failureEntries
            .map(
              ({ label, exchange, fiat, side: entrySide, error }) =>
                `${label || `${exchange} ${fiat}`} (${entrySide}): ${error}`
            )
            .join(" | ");
          console.error(`P2P quotes apresentaram erros: ${message}`);
        }
        const combined = combos.map((combo, index) => {
          const sellEntry = sellEntries[index];
          const buyEntry = buyEntries[index];
          return {
            exchange: combo.exchange,
            fiat: combo.fiat,
            label: combo.label,
            experimental: combo.experimental,
            sellQuote: sellEntry?.quote ?? null,
            sellError: sellEntry?.error ?? null,
            buyQuote: buyEntry?.quote ?? null,
            buyError: buyEntry?.error ?? null,
          };
        });
        const failed = failureEntries.length > 0;
        setData(combined);
        setHasFailures(failed);
        return combined;
      } finally {
        setLoading(false);
      }
    },
    [combos, limit, fetchSideData]
  );

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const refresh = useCallback(() => fetchQuotes(true), [fetchQuotes]);

  return {
    data,
    loading,
    hasFailures,
    refresh,
  };
}
